import React, { useMemo, useState } from 'react';
import { FormattedMessage } from '../../../../util/reactIntl';
import { obfuscatedCoordinates } from '../../../../util/maps';
import { Map } from '../../../../components';
import config from '../../../../config';
import BulletPointsFormatter from '../../../../util/formatters/bulletPointsFormatter/bulletPointsFormatter';
import Modal from '../Utils/Modal/Modal';
import css from './MapSection.module.css';

const FALLBACK_GERMAN = 'Übersetzung hinzufügen';
const PREVIEW_SENTENCE_COUNT = 3;

const normalizeArrivalText = value => {
  if (value == null || value === FALLBACK_GERMAN) return '';
  if (Array.isArray(value)) return value.filter(Boolean).join('. ');
  if (typeof value === 'string') return value.replace(/\\n/g, '\n').trim();
  return '';
};

const splitSentences = text =>
  text
    .split('. ')
    .map(sentence => sentence.trim())
    .filter(Boolean);

const MapSection = ({
  geolocation,
  publicData,
  listingId,
  howToGetThereEn = '',
  howToGetThereDe = '',
  currentLanguage = 'de',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const arrivalText = useMemo(() => {
    const text =
      currentLanguage === 'de'
        ? normalizeArrivalText(howToGetThereDe) || normalizeArrivalText(howToGetThereEn)
        : normalizeArrivalText(howToGetThereEn) || normalizeArrivalText(howToGetThereDe);
    return text;
  }, [howToGetThereEn, howToGetThereDe, currentLanguage]);

  const { previewText, hasMore } = useMemo(() => {
    if (!arrivalText) return { previewText: '', hasMore: false };
    const sentences = splitSentences(arrivalText);
    if (sentences.length <= PREVIEW_SENTENCE_COUNT) {
      return { previewText: arrivalText, hasMore: false };
    }
    return {
      previewText: `${sentences.slice(0, PREVIEW_SENTENCE_COUNT).join('. ')}.`,
      hasMore: true,
    };
  }, [arrivalText]);

  if (!geolocation) {
    return null;
  }

  const address = publicData?.location?.address || '';
  const cacheKey = listingId ? `${listingId.uuid}_${geolocation.lat}_${geolocation.lng}` : null;

  const mapProps = config.maps.fuzzy.enabled
    ? { obfuscatedCenter: obfuscatedCoordinates(geolocation, cacheKey) }
    : { address, center: geolocation };

  return (
    <section className={css.section} data-elbaction="visible:vis location">
      <h2 className={css.title}>
        <FormattedMessage id="MapSection.title" />
      </h2>

      <div className={css.mapWrap}>
        <div className={css.map}>
          <Map {...mapProps} useStaticMap={false} />
        </div>
      </div>

      {arrivalText && (
        <p className={css.travelText}>
          {previewText}
          {hasMore && (
            <>
              {' '}
              <button
                type="button"
                className={css.moreLink}
                onClick={() => setIsModalOpen(true)}
                data-elbaction="click:click howtogetthere"
              >
                <FormattedMessage id="MapSection.moreArrival" />
              </button>
            </>
          )}
        </p>
      )}

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
          <h2>
            <FormattedMessage id="EditListingLocationForm.arrival" />
          </h2>
          <div className={css.modalContent}>
            <BulletPointsFormatter text={arrivalText} />
          </div>
        </Modal>
      )}
    </section>
  );
};

export default MapSection;
